import * as THREE from "three";
import { B, type World } from "../shared/world";
import { travel, type ProjWorld } from "../shared/projectile";
import type { ProjInfo } from "../shared/protocol";
import { itemGeometry, itemMaterial, type Particles } from "./entities";
import { pixelTexture } from "./textures";

// Arrows in flight and stuck in things, and fishing bobbers on their lines.
// The server decides where they land; in between its messages we fly them
// along the same arc so they look smooth.

interface View {
  info: ProjInfo;
  root: THREE.Object3D;
  /** Locally simulated position and velocity. */
  p: { x: number; y: number; z: number; vx: number; vy: number; vz: number };
  landed: boolean;
  line?: THREE.Line;
}

/** Minecraft's arrow is two crossed planes; ours are two crossed copies of the extruded item sprite. */
function arrowMesh(): THREE.Object3D {
  const root = new THREE.Group();
  for (const roll of [0, Math.PI / 2]) {
    const spin = new THREE.Group();
    spin.rotation.z = roll;
    const along = new THREE.Group();
    along.rotation.y = -Math.PI / 2; // sprite +x → +z, which lookAt points along the flight
    along.position.z = -0.2; // the position is the tip, so a stuck arrow pokes out of the block
    const sprite = new THREE.Mesh(itemGeometry("arrow"), itemMaterial);
    sprite.rotation.z = -Math.PI / 4; // the sprite's diagonal shaft → +x
    sprite.scale.setScalar(0.55);
    along.add(sprite);
    spin.add(along);
    root.add(spin);
  }
  return root;
}

// The red-and-white float, always facing you like the real one.
const BOBBER = ["..kkkk..", ".krrrrk.", "krRrrrrk", "krrrrrrk", "kwwwwwwk", "kwwwwwwk", ".kwwwwk.", "..kkkk.."];
let bobberMat: THREE.SpriteMaterial | null = null;
function bobberMesh(): THREE.Object3D {
  if (!bobberMat) {
    const c = document.createElement("canvas");
    c.width = c.height = 8;
    const g = c.getContext("2d")!;
    const col: Record<string, string> = { k: "#1a1a1a", r: "#d42a2a", R: "#ff7a7a", w: "#f0f0f0" };
    BOBBER.forEach((row, y) => [...row].forEach((ch, x) => {
      if (!col[ch]) return;
      g.fillStyle = col[ch]!;
      g.fillRect(x, y, 1, 1);
    }));
    bobberMat = new THREE.SpriteMaterial({ map: pixelTexture(c), alphaTest: 0.5 });
  }
  const s = new THREE.Sprite(bobberMat);
  s.scale.setScalar(0.22);
  return s;
}

const LINE_SEGMENTS = 16;
const lineMat = new THREE.LineBasicMaterial({ color: 0x161616 });

export class ProjectileView {
  group = new THREE.Group();
  private list = new Map<number, View>();
  private world: ProjWorld | null = null;
  private clock = 0;

  constructor(private particles: Particles) {}

  setWorld(w: World, solid: (x: number, y: number, z: number) => boolean) {
    this.world = {
      solid,
      water: (x, y, z) => w.get(x, y, z) === B.WATER,
      outside: (x, y, z) => x < 0 || z < 0 || x >= w.sx || z >= w.sz || y < -2,
    };
  }

  /** A new projectile, or the server telling us where one ended up. Returns true if it's new. */
  set(info: ProjInfo): boolean {
    let v = this.list.get(info.id);
    const fresh = !v;
    if (!v) {
      v = { info, root: info.kind === "arrow" ? arrowMesh() : bobberMesh(), p: { ...info }, landed: false };
      if (info.kind === "bobber") {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.Float32BufferAttribute(new Float32Array((LINE_SEGMENTS + 1) * 3), 3));
        v.line = new THREE.Line(geo, lineMat);
        v.line.frustumCulled = false;
        this.group.add(v.line);
      }
      this.list.set(info.id, v);
      this.group.add(v.root);
    }
    v.info = info;
    v.p = { x: info.x, y: info.y, z: info.z, vx: info.vx, vy: info.vy, vz: info.vz };
    v.landed = info.state !== 0;
    this.place(v);
    return fresh;
  }

  remove(id: number): ProjInfo | null {
    const v = this.list.get(id);
    if (!v) return null;
    this.group.remove(v.root);
    if (v.line) {
      this.group.remove(v.line);
      v.line.geometry.dispose();
    }
    this.list.delete(id);
    return { ...v.info, x: v.p.x, y: v.p.y, z: v.p.z };
  }

  clear() {
    for (const id of [...this.list.keys()]) this.remove(id);
  }

  /** Does this player have a bobber out? (Their rod is drawn without its dangling line then.) */
  hasBobber(by: number): boolean {
    for (const v of this.list.values()) if (v.info.kind === "bobber" && v.info.by === by) return true;
    return false;
  }

  /**
   * rodTip: where a player's line leaves their rod. playerAt: where a player
   * is standing, for bobbers hooked onto them.
   */
  update(dt: number, rodTip: (id: number) => THREE.Vector3 | null, playerAt: (id: number) => THREE.Vector3 | null) {
    this.clock += dt;
    for (const v of this.list.values()) {
      const kind = v.info.kind;
      if (v.info.state === 2 && v.info.hook !== undefined) {
        const at = playerAt(v.info.hook);
        if (at) Object.assign(v.p, { x: at.x, y: at.y + 1, z: at.z });
      } else if (!v.landed && this.world) {
        // Stop where it hits; the server will confirm the exact spot (or that it hit someone).
        if (travel(v.p, kind, dt, this.world)) v.landed = true;
        if (v.info.crit) this.particles.spark(v.p.x, v.p.y, v.p.z);
      }
      this.place(v);
      if (v.line) this.drawLine(v, rodTip(v.info.by));
    }
  }

  private place(v: View) {
    const { x, y, z, vx, vy, vz } = v.p;
    if (v.info.kind === "bobber") {
      // Floating bobbers bob.
      const bob = v.info.state === 1 ? Math.sin(this.clock * 3 + v.info.id) * 0.04 : 0;
      v.root.position.set(x, y + 0.08 + bob, z);
      return;
    }
    v.root.position.set(x, y, z);
    if (vx || vy || vz) v.root.lookAt(x + vx, y + vy, z + vz);
  }

  /** The fishing line: from the rod tip to the bobber, sagging when there's slack. */
  private drawLine(v: View, tip: THREE.Vector3 | null) {
    const line = v.line!;
    line.visible = !!tip;
    if (!tip) return;
    const end = v.root.position;
    const sag = v.info.state === 0 ? 0.05 : Math.min(1.2, tip.distanceTo(end) * 0.08);
    const pos = line.geometry.getAttribute("position") as THREE.BufferAttribute;
    for (let i = 0; i <= LINE_SEGMENTS; i++) {
      const t = i / LINE_SEGMENTS;
      pos.setXYZ(i, tip.x + (end.x - tip.x) * t, tip.y + (end.y - tip.y) * t - sag * 4 * t * (1 - t), tip.z + (end.z - tip.z) * t);
    }
    pos.needsUpdate = true;
  }
}
